/**
 * Меню телефона: усе, що на великому екрані живе у верхній панелі, крім
 * щоденних вкладок — вони лишилися в шапці кермом на чотири розділи. Сюди ж
 * потрапили ті розділи, у які заходять не щодня (налаштування, «Про
 * програму»). Сторінка може додати свій блок зверху (`extra`): так у таблиці
 * сюди потрапляють календар і дії.
 */

import { useEffect, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Modal } from './ui.js';
import { isMobileTab, markSpaceNav, useNavItems } from './nav.js';
import { useCan, useData } from '../store/data.js';
import { useLink } from '../store/link.js';
import { useLive } from '../store/live.js';
import { useMarks } from '../store/marks.js';
import { themeOriginFromClick, useUi } from '../store/ui.js';

const THEME_LABEL = { dark: 'Темна', light: 'Світла' } as const;

export function MobileMenu({ extra, onClose }: { extra?: ReactNode; onClose: () => void }): ReactNode {
  const { session, orgs, logout, switchOrg } = useData();
  const canOrgs = useCan('orgs.manage');
  const theme = useUi(s => s.theme);
  const setTheme = useUi(s => s.setTheme);
  const { pathname } = useLocation();
  const aside = useNavItems().filter(i => !isMobileTab(i));
  const setUpdates = useUi(s => s.setUpdates);
  const online = useLink(s => s.online);
  const syncing = useLink(s => s.syncing);
  const pending = useLink(s => s.outbox + s.marksPending);
  const present = useLive(s => s.online);
  const connected = useLive(s => s.connected);
  // Меню відкрили — перепитуємо, хто в програмі, як і картка в шапці.
  useEffect(() => void useLive.getState().fetchPresence(), []);
  const pingMs = useLink(s => s.pingMs);
  const server = useLink(s => s.server);

  const mode = !online ? 'off' : syncing || pending ? 'sync' : 'on';
  const linkLabel = !online
    ? pending
      ? `Немає зв’язку, ${pending} змін чекають`
      : 'Немає зв’язку з сервером'
    : server && !server.db
      ? 'База даних не відповідає'
      : pending
        ? 'Надсилаю збережені зміни…'
        : 'Сервер підключено';

  return (
    <Modal title="Меню" onClose={onClose}>
      <div className="mmenu">
        {/* Спершу те, заради чого меню відкривають на сторінці (місяць і дії
            таблиці), а вже потім розділи й решта. */}
        {extra}

        {aside.length ? (
          <div className="mmenu-block">
            <div className="mmenu-title">Розділи</div>
            {aside.map(i => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                viewTransition
                className={({ isActive }) => `mmenu-row${isActive ? ' active' : ''}`}
                onClick={() => {
                  markSpaceNav(pathname, i.to);
                  onClose();
                }}
              >
                <span>{i.label}</span>
                <span className="mmenu-go" aria-hidden="true">
                  ›
                </span>
              </NavLink>
            ))}
          </div>
        ) : null}

        {connected ? (
          <div className="mmenu-block">
            <div className="mmenu-title">У програмі: {present.length}</div>
            {present.map(u => (
              <div key={u.userId} className="mmenu-row static">
                <span className="presence-dot" />
                <span>
                  {u.fullName || u.login}
                  {u.userId === session?.user.id ? <span className="presence-me">, це ви</span> : null}
                </span>
                <b>{u.login}</b>
              </div>
            ))}
          </div>
        ) : null}

        <div className="mmenu-block">
          <div className="mmenu-title">Програма</div>
          {canOrgs && orgs.length > 1 ? (
            <label className="mmenu-row">
              <span>Курс</span>
              <select
                className="select"
                value={session?.org.id ?? ''}
                onChange={e => void switchOrg(e.target.value)}
              >
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.shortName}, {o.people}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            /* Значок курсу з шапки на телефоні сховано — але бачити, у якому
               курсі працюєш, усе одно треба. */
            <div className="mmenu-row static">
              <span>Курс</span>
              <b>{session?.org.shortName}</b>
            </div>
          )}
          <button
            type="button"
            className="mmenu-row"
            onClick={e => setTheme(theme === 'dark' ? 'light' : 'dark', themeOriginFromClick(e))}
          >
            <span>Тема оформлення</span>
            <b>{THEME_LABEL[theme]}</b>
          </button>
          {/* Оновлення в шапці — значок, а на телефоні значків там немає. */}
          <button
            type="button"
            className="mmenu-row"
            onClick={() => {
              onClose();
              setUpdates(true);
            }}
          >
            <span>Оновлення</span>
            <span className="mmenu-go" aria-hidden="true">
              ›
            </span>
          </button>
          <button
            type="button"
            className="mmenu-row"
            onClick={() => {
              void useLink
                .getState()
                .ping(true)
                .then(ok => {
                  if (ok) {
                    void useMarks.getState().flush();
                    void useData.getState().refresh();
                  }
                });
            }}
          >
            <span className={`link-lamp ${mode}`} />
            <span>{linkLabel}</span>
            {online && pingMs !== null ? <b>{pingMs} мс</b> : null}
          </button>
          <button
            type="button"
            className="mmenu-row danger"
            onClick={() => {
              onClose();
              void logout();
            }}
          >
            Вийти
          </button>
        </div>
      </div>
    </Modal>
  );
}
